import React from "react";
import {
  Box,
  Drawer,
  IconButton,
  Typography,
  Stack,
  Divider,
  Chip,
  Button,
  CircularProgress,
  Grid,
  useTheme,
  Alert,
  Paper,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Accordion, AccordionSummary, AccordionDetails } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import EuroIcon from "@mui/icons-material/Euro";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import CampaignIcon from "@mui/icons-material/Campaign";
import LabelIcon from "@mui/icons-material/Label";
import CategoryIcon from "@mui/icons-material/Category";
import PercentIcon from "@mui/icons-material/Percent";
import { useGetBriefingByIdQuery } from "../../store/apis/briefingApi";
import { useUserRole } from "../../hooks/useUserRole";
import { canViewGoalField } from "../../utils/briefingPermissions";
import { BriefingStatusChip, CampaignStatusChip } from "../common/StatusChips";
import CampaignDateRange from "./CampaignDateRange";

interface BriefingSummaryDrawerProps {
  open: boolean;
  onClose: () => void;
  briefingId: string | null;
  canEdit: boolean;
}

const BriefingSummaryDrawer: React.FC<BriefingSummaryDrawerProps> = ({
  open,
  onClose,
  briefingId,
  canEdit,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { role } = useUserRole();

  const { data, isLoading, error } = useGetBriefingByIdQuery(
    { id: briefingId! },
    {
      skip: !briefingId || !open,
      refetchOnMountOrArgChange: true,
    }
  );

  const briefing = data?.data;

  const handleNavigateToDetail = () => {
    if (briefingId) {
      navigate(`/briefings/${briefingId}`);
      onClose();
    }
  };

  const handleNavigateToEdit = () => {
    if (briefingId) {
      navigate(`/briefings/${briefingId}/edit`);
      onClose();
    }
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (error || !briefing) {
      return (
        <Box sx={{ p: 3 }}>
          <Alert severity="error">
            {t(
              "briefings.errorLoadingDetail",
              "Failed to load briefing details"
            )}
          </Alert>
        </Box>
      );
    }

    return (
      <Stack spacing={3} sx={{ p: 3 }}>
        {/* General Info Grid & Financials (KPI CARDS) */}
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 2,
                height: "100%",
                display: "flex",
                flexDirection: "column",
                gap: 1,
                transition: "box-shadow 0.2s",
                "&:hover": { boxShadow: theme.shadows[2] },
              }}
            >
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                color="primary.main"
              >
                <AccountBalanceWalletIcon fontSize="small" />
                <Typography variant="subtitle2" fontWeight={600}>
                  {t("briefingsCreate.form.totalBudget", "Total budget")}
                </Typography>
              </Stack>
              <Typography variant="h6" fontWeight={700}>
                {(briefing.totalBudget ?? 0).toLocaleString()}{" "}
                <EuroIcon
                  fontSize="small"
                  sx={{ verticalAlign: "middle", mb: "2px" }}
                />
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 2,
                height: "100%",
                display: "flex",
                flexDirection: "column",
                gap: 1,
                transition: "box-shadow 0.2s",
                "&:hover": { boxShadow: theme.shadows[2] },
              }}
            >
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                color="secondary.main"
              >
                <PercentIcon fontSize="small" />
                <Typography variant="subtitle2" fontWeight={600}>
                  {t("briefingsCreate.form.fee", "Fee")}
                </Typography>
              </Stack>
              <Typography variant="h6" fontWeight={700}>
                {new Intl.NumberFormat(undefined, {
                  style: "percent",
                  minimumFractionDigits: 2,
                }).format((briefing.fee ?? 0) / 100)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Total+Fee:{" "}
                {new Intl.NumberFormat("es-ES", {
                  style: "currency",
                  currency: "EUR",
                }).format(
                  (briefing.totalBudget ?? 0) * (1 + (briefing.fee ?? 0) / 100)
                )}
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 2,
                height: "100%",
                display: "flex",
                flexDirection: "column",
                gap: 1,
                transition: "box-shadow 0.2s",
                "&:hover": { boxShadow: theme.shadows[2] },
              }}
            >
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                color="info.main"
              >
                <CalendarMonthIcon fontSize="small" />
                <Typography variant="subtitle2" fontWeight={600}>
                  {t("briefingsView.period", "Period")}
                </Typography>
              </Stack>
              <CampaignDateRange
                startDate={briefing.campaignsStartDate}
                endDate={briefing.campaignsEndDate}
                month={briefing.month}
                year={briefing.year}
                variant="h6"
              />
            </Paper>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 2,
                height: "100%",
                display: "flex",
                flexDirection: "column",
                gap: 1,
                transition: "box-shadow 0.2s",
                "&:hover": { boxShadow: theme.shadows[2] },
              }}
            >
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                color="text.secondary"
              >
                <CampaignIcon fontSize="small" />
                <Typography variant="subtitle2" fontWeight={600}>
                  {t("briefings.campaignName", "Campaign Name")}
                </Typography>
              </Stack>
              <Typography variant="body2" fontWeight={600}>
                {briefing.campaignName || "-"}
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 2,
                height: "100%",
                display: "flex",
                flexDirection: "column",
                gap: 1,
                transition: "box-shadow 0.2s",
                "&:hover": { boxShadow: theme.shadows[2] },
              }}
            >
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                color="text.secondary"
              >
                <LabelIcon fontSize="small" />
                <Typography variant="subtitle2" fontWeight={600}>
                  {t("briefings.brandOrProduct", "Brand/Product")}
                </Typography>
              </Stack>
              <Typography variant="body2">
                {briefing.brandOrProduct || "-"}
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 2,
                height: "100%",
                display: "flex",
                flexDirection: "column",
                gap: 1,
                transition: "box-shadow 0.2s",
                "&:hover": { boxShadow: theme.shadows[2] },
              }}
            >
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                color="text.secondary"
              >
                <CategoryIcon fontSize="small" />
                <Typography variant="subtitle2" fontWeight={600}>
                  {t("briefings.type", "Type")}
                </Typography>
              </Stack>
              <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
                {t(
                  `briefingsCreate.form.briefingTypeOptions.${briefing.briefingType}`,
                  briefing.briefingType
                )}
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 2,
                height: "100%",
                display: "flex",
                flexDirection: "column",
                gap: 1,
                transition: "box-shadow 0.2s",
                "&:hover": { boxShadow: theme.shadows[2] },
              }}
            >
              <Typography
                variant="subtitle2"
                color="text.secondary"
                fontWeight={600}
                sx={{ mb: "auto" }}
              >
                {t("briefings.table.status", "Status")}
              </Typography>
              <Box>
                <BriefingStatusChip status={briefing.status} />
              </Box>
            </Paper>
          </Grid>
        </Grid>

        <Divider />

        {/* Associated Campaigns */}
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 700 }}>
            {t("briefings.associatedCampaigns", "Associated Campaigns")} (
            {briefing.associatedCampaigns?.length || 0})
          </Typography>

          {briefing.associatedCampaigns?.length === 0 ? (
            <Typography
              variant="body2"
              color="text.secondary"
              fontStyle="italic"
            >
              {t("briefings.noCampaigns", "No campaigns associated")}
            </Typography>
          ) : (
            <Box>
              {briefing.associatedCampaigns.map((campaign) => (
                <Accordion
                  key={campaign.id}
                  disableGutters
                  elevation={0}
                  sx={{
                    border: `1px solid ${theme.palette.divider}`,
                    "&:not(:last-child)": {
                      borderBottom: 0,
                    },
                    "&:before": {
                      display: "none",
                    },
                  }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      width="100%"
                    >
                      <Typography
                        variant="subtitle2"
                        fontWeight={600}
                        sx={{
                          wordBreak: "break-word",
                          flexGrow: 1,
                        }}
                      >
                        {campaign.name}
                      </Typography>
                      <CampaignStatusChip
                        status={campaign.status}
                        sx={{ height: 20, fontSize: "0.7rem" }}
                      />
                      <Chip
                        label={campaign.platform}
                        size="small"
                        sx={{ height: 20, fontSize: "0.7rem" }}
                      />
                      {canEdit && (
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (briefingId) {
                              navigate(
                                `/briefings/${briefingId}/associated-campaigns/${campaign.id}`,
                                { state: { isEditMode: true } }
                              );
                            }
                          }}
                          sx={{ ml: "auto", mr: 1 }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Stack>
                  </AccordionSummary>
                  <AccordionDetails
                    sx={{ bgcolor: theme.palette.grey[50], p: 2 }}
                  >
                    <Stack spacing={2}>
                      {/* Goal Section */}
                      <Box>
                        <Divider textAlign="left" sx={{ mb: 1 }}>
                          <Typography variant="caption" color="text.secondary">
                            {t("briefings.goal", "Goal")}
                          </Typography>
                        </Divider>
                        <Grid container spacing={1}>
                          <Grid item xs={6}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              display="block"
                            >
                              {t("briefings.campaignDates", "Dates")}
                            </Typography>
                            <Typography variant="body2">
                              {campaign.goal?.startDate &&
                              campaign.goal?.endDate
                                ? `${dayjs(campaign.goal.startDate).format("DD/MM/YY")} - ${dayjs(campaign.goal.endDate).format("DD/MM/YY")}`
                                : "-"}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              display="block"
                            >
                              {t("briefings.campaignObjective", "Objective")}
                            </Typography>
                            <Typography variant="body2">
                              {campaign.goal?.objective || "-"}
                            </Typography>
                          </Grid>
                          {role &&
                            canViewGoalField(role, "platformInversion") && (
                              <Grid item xs={6}>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  display="block"
                                >
                                  {t(
                                    "briefings.platformInversion",
                                    "Platform Inversion"
                                  )}
                                </Typography>
                                <Typography variant="body2">
                                  {new Intl.NumberFormat(undefined, {
                                    style: "currency",
                                    currency: campaign.goal?.currency || "EUR",
                                  }).format(
                                    campaign.goal?.platformInversion || 0
                                  )}
                                </Typography>
                              </Grid>
                            )}
                          <Grid item xs={6}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              display="block"
                            >
                              {t("briefings.expectedVolume", "Expected Volume")}
                            </Typography>
                            <Typography variant="body2">
                              {new Intl.NumberFormat("es-ES", {
                                minimumFractionDigits: 0,
                                maximumFractionDigits: 2,
                              }).format(campaign.goal?.expectedVolume || 0)}
                            </Typography>
                          </Grid>
                          {role && canViewGoalField(role, "netUnitCost") && (
                            <Grid item xs={6}>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                display="block"
                              >
                                {t("briefings.netUnitCost", "Net Unit Cost")}
                              </Typography>
                              <Typography variant="body2">
                                {new Intl.NumberFormat("es-ES", {
                                  style: "currency",
                                  currency: campaign.goal?.currency || "EUR",
                                  minimumFractionDigits: 4,
                                  maximumFractionDigits: 4,
                                }).format(campaign.goal?.netUnitCost || 0)}
                              </Typography>
                            </Grid>
                          )}
                        </Grid>
                      </Box>

                      {/* Target Section */}
                      {campaign.target && (
                        <Box>
                          <Divider textAlign="left" sx={{ mb: 1 }}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {t("briefings.target", "Target")}
                            </Typography>
                          </Divider>
                          <Grid container spacing={1}>
                            <Grid item xs={6}>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                display="block"
                              >
                                {t("briefings.geography", "Geography")}
                              </Typography>
                              <Typography variant="body2">
                                {campaign.target.geography || "-"}
                              </Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                display="block"
                              >
                                {t("briefings.demographics", "Demographics")}
                              </Typography>
                              <Typography variant="body2">
                                {campaign.target.sex || "All"} •{" "}
                                {campaign.target.minimumAge}-
                                {campaign.target.maximumAge}
                              </Typography>
                            </Grid>
                          </Grid>
                        </Box>
                      )}

                      {/* Assets Section */}
                      {campaign.asset && (
                        <Box>
                          <Divider textAlign="left" sx={{ mb: 1 }}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {t("briefings.assets", "Assets")}
                            </Typography>
                          </Divider>
                          <Grid container spacing={1}>
                            <Grid item xs={12}>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                display="block"
                              >
                                {t(
                                  "briefings.destinationUrls",
                                  "Destination URLs"
                                )}
                              </Typography>
                              {(campaign.asset.destinations ?? []).length ===
                              0 ? (
                                <Typography variant="body2">-</Typography>
                              ) : (
                                <Stack spacing={0.75} sx={{ mt: 0.5 }}>
                                  {(campaign.asset.destinations ?? []).map(
                                    (destination, index) => (
                                      <Box key={destination.id ?? index}>
                                        {destination.label && (
                                          <Typography
                                            variant="caption"
                                            fontWeight={700}
                                            display="block"
                                          >
                                            {destination.label}
                                          </Typography>
                                        )}
                                        <Typography
                                          variant="body2"
                                          sx={{
                                            wordBreak: "break-all",
                                            fontSize: "0.8rem",
                                          }}
                                        >
                                          {destination.url ? (
                                            <a
                                              href={destination.url}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              style={{
                                                color:
                                                  theme.palette.primary.main,
                                                textDecoration: "none",
                                              }}
                                            >
                                              {destination.url}
                                            </a>
                                          ) : (
                                            "-"
                                          )}
                                        </Typography>
                                      </Box>
                                    )
                                  )}
                                </Stack>
                              )}
                            </Grid>
                          </Grid>
                        </Box>
                      )}
                    </Stack>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          )}
        </Box>

        {/* Notes */}
        {briefing.notes && (
          <Box>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
              {t("briefings.notes", "Notes")}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                whiteSpace: "pre-wrap",
                bgcolor: theme.palette.grey[50],
                p: 1.5,
                borderRadius: 1,
              }}
            >
              {briefing.notes}
            </Typography>
          </Box>
        )}
      </Stack>
    );
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 500 },
          p: 0,
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 3,
          borderBottom: `1px solid ${theme.palette.divider}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          bgcolor: theme.palette.background.default,
        }}
      >
        <Typography
          variant="h6"
          fontWeight={700}
          noWrap
          sx={{ maxWidth: "80%" }}
        >
          {briefing?.name || t("briefings.summary", "Briefing Summary")}
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Scrollable Content */}
      <Box sx={{ flexGrow: 1, overflowY: "auto" }}>{renderContent()}</Box>

      {/* Footer Actions */}
      <Box
        sx={{
          p: 3,
          borderTop: `1px solid ${theme.palette.divider}`,
          display: "flex",
          gap: 2,
          bgcolor: theme.palette.background.default,
        }}
      >
        <Button
          fullWidth
          variant="outlined"
          startIcon={<OpenInNewIcon />}
          onClick={handleNavigateToDetail}
        >
          {t("common.viewFullDetails", "View Full Details")}
        </Button>
        {canEdit && (
          <Button
            fullWidth
            variant="contained"
            startIcon={<EditIcon />}
            onClick={handleNavigateToEdit}
          >
            {t("common.edit", "Edit")}
          </Button>
        )}
      </Box>
    </Drawer>
  );
};

export default BriefingSummaryDrawer;
